import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Search, X } from "lucide-react";
import sensorImage from "../assets/images/ssm.jpeg";
import SensorClusterGroup from "../components/maps/SensorClusterGroup";

const DEFAULT_CENTER = [34.0, 9.0];

const MapViewport = ({ sensors }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || sensors.length === 0) return;

    const timer = window.setTimeout(() => {
      map.invalidateSize();

      if (sensors.length === 1) {
        map.setView([sensors[0].latitude, sensors[0].longitude], 16, {
          animate: false,
        });
        return;
      }

      const bounds = L.latLngBounds(
        sensors.map((sensor) => [sensor.latitude, sensor.longitude])
      );
      map.fitBounds(bounds, { padding: [48, 48], maxZoom: 15 });
    }, 80);

    return () => window.clearTimeout(timer);
  }, [map, sensors]);

  return null;
};

const FlyToSelectedSensor = ({ sensor, markerRefs }) => {
  const map = useMap();

  useEffect(() => {
    if (!sensor || !map) return;

    map.flyTo([Number(sensor.latitude), Number(sensor.longitude)], 16, {
      duration: 0.75,
    });

    const timer = window.setTimeout(() => {
      markerRefs.current[sensor.sensorId]?.openPopup();
    }, 400);

    return () => window.clearTimeout(timer);
  }, [sensor, map, markerRefs]);

  return null;
};

const createSensorIcon = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  const statusClass = normalizedStatus.includes("attention")
    ? "is-attention"
    : "is-online";

  return L.divIcon({
    className: "admin-overview-sensor-div-icon",
    html: `<span class="admin-overview-sensor-marker ${statusClass}"><img src="${sensorImage}" alt="" /><span class="admin-overview-sensor-marker-status"></span></span>`,
    iconSize: [48, 66],
    iconAnchor: [24, 62],
    popupAnchor: [0, -58],
  });
};

const formatDate = (value, language) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat(language || "fr", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const AdminSensorsMap = ({ sensors = [], loading, error, onRetry, t, language }) => {
  const [baseLayer, setBaseLayer] = useState("standard");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSensor, setSelectedSensor] = useState(null);
  const markerRefs = useRef({});

  const validSensors = useMemo(
    () =>
      sensors.filter((sensor) => {
        const latitude = Number(sensor.latitude);
        const longitude = Number(sensor.longitude);
        return (
          Number.isFinite(latitude) &&
          Number.isFinite(longitude) &&
          latitude >= -90 &&
          latitude <= 90 &&
          longitude >= -180 &&
          longitude <= 180
        );
      }),
    [sensors]
  );

  const sensorIcons = useMemo(() => {
    const icons = new Map();
    validSensors.forEach((sensor) => {
      icons.set(sensor.sensorId, createSensorIcon(sensor.sensorStatus));
    });
    return icons;
  }, [validSensors]);

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];

    return validSensors
      .filter((sensor) => {
        const haystack = [sensor.sensorName, sensor.sensorCode, sensor.ownerName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      })
      .slice(0, 8);
  }, [validSensors, searchQuery]);

  const handleSelectSearchResult = (sensor) => {
    setSelectedSensor(sensor);
    setSearchQuery("");
  };

  if (loading && validSensors.length === 0) {
    return (
      <div className="admin-overview-map-loading" aria-live="polite">
        <span className="admin-overview-map-spinner" aria-hidden="true" />
        <span>{t("loading")}</span>
      </div>
    );
  }

  if (error && validSensors.length === 0) {
    return (
      <div className="admin-overview-map-message" role="alert">
        <span>{error}</span>
        <button type="button" onClick={onRetry}>
          {t("admin_overview_map_retry")}
        </button>
      </div>
    );
  }

  if (validSensors.length === 0) {
    return (
      <div className="admin-overview-map-message">
        {t("admin_overview_map_empty")}
      </div>
    );
  }

  return (
    <div className="admin-overview-map-wrap">
      <MapContainer
        className="admin-overview-map"
        center={DEFAULT_CENTER}
        zoom={6}
        minZoom={3}
        maxZoom={19}
        scrollWheelZoom
      >
        <TileLayer
          key={baseLayer}
          attribution={
            baseLayer === "satellite"
              ? "Tiles &copy; Esri"
              : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          }
          url={
            baseLayer === "satellite"
              ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          }
        />
        <MapViewport sensors={validSensors} />
        <FlyToSelectedSensor sensor={selectedSensor} markerRefs={markerRefs} />

        <SensorClusterGroup>
          {validSensors.map((sensor) => {
            const lat = Number(sensor.latitude);
            const lng = Number(sensor.longitude);
            const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

            return (
              <Marker
                key={sensor.sensorId}
                position={[lat, lng]}
                icon={sensorIcons.get(sensor.sensorId)}
                ref={(marker) => {
                  if (marker) markerRefs.current[sensor.sensorId] = marker;
                }}
              >
                <Popup minWidth={245}>
                  <div className="admin-overview-map-popup">
                    <img src={sensorImage} alt="" />
                    <div className="admin-overview-map-popup-heading">
                      <strong>
                        {sensor.sensorName || sensor.sensorCode || "-"}
                      </strong>
                      <span>{sensor.sensorCode || "-"}</span>
                    </div>
                    <dl>
                      <div>
                        <dt>{t("admin_overview_sensor_field")}</dt>
                        <dd>{sensor.fieldName || "-"}</dd>
                      </div>
                      <div>
                        <dt>{t("admin_overview_sensor_farm")}</dt>
                        <dd>{sensor.farmName || "-"}</dd>
                      </div>
                      <div>
                        <dt>{t("admin_overview_sensor_owner")}</dt>
                        <dd>{sensor.ownerName || "-"}</dd>
                      </div>
                      <div>
                        <dt>{t("admin_overview_sensor_added_on")}</dt>
                        <dd>{formatDate(sensor.createdAt, language)}</dd>
                      </div>
                    </dl>
                    <a
                      className="admin-overview-map-popup-gmaps"
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("admin_overview_map_open_google_maps", {
                        defaultValue: "Open in Google Maps",
                      })}
                    </a>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </SensorClusterGroup>
      </MapContainer>

      <div className="admin-overview-map-search" role="search">
        <Search className="admin-overview-map-search-icon" size={16} aria-hidden="true" />
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t("admin_overview_map_search_placeholder", {
            defaultValue: "Search a user or a sensor",
          })}
          aria-label={t("admin_overview_map_search_placeholder", {
            defaultValue: "Search a user or a sensor",
          })}
        />
        {searchQuery && (
          <button
            type="button"
            className="admin-overview-map-search-clear"
            onClick={() => setSearchQuery("")}
            aria-label={t("clear", { defaultValue: "Clear" })}
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
        {searchQuery.trim() && (
          <ul className="admin-overview-map-search-results">
            {searchResults.length === 0 ? (
              <li className="admin-overview-map-search-empty">
                {t("admin_overview_map_search_no_results", {
                  defaultValue: "No results",
                })}
              </li>
            ) : (
              searchResults.map((sensor) => (
                <li key={sensor.sensorId}>
                  <button
                    type="button"
                    onClick={() => handleSelectSearchResult(sensor)}
                  >
                    <strong>{sensor.sensorName || sensor.sensorCode}</strong>
                    <span>{sensor.ownerName || sensor.fieldName || "-"}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      <div
        className="admin-overview-map-layer-switch"
        role="group"
        aria-label={t("admin_overview_map_layer_selector")}
      >
        <button
          type="button"
          className={baseLayer === "standard" ? "is-active" : ""}
          aria-pressed={baseLayer === "standard"}
          onClick={() => setBaseLayer("standard")}
        >
          {t("admin_overview_map_layer_standard")}
        </button>
        <button
          type="button"
          className={baseLayer === "satellite" ? "is-active" : ""}
          aria-pressed={baseLayer === "satellite"}
          onClick={() => setBaseLayer("satellite")}
        >
          {t("admin_overview_map_layer_satellite")}
        </button>
      </div>

      {loading && (
        <div className="admin-overview-map-refreshing" aria-live="polite">
          <span className="admin-overview-map-spinner" aria-hidden="true" />
          {t("loading")}
        </div>
      )}
    </div>
  );
};

export default AdminSensorsMap;
